import { useEffect, useRef, useState } from "react";
import { Activity } from "lucide-react";
import { wsUrl } from "@/lib/api";

function eventTime(ts) {
  try {
    return new Date(ts).toLocaleTimeString("en-ZA", { hour12: false, timeZone: "Africa/Johannesburg" });
  } catch {
    return "";
  }
}

export function LiveFeed({ initial = [], testid = "live-feed" }) {
  const [events, setEvents] = useState(initial);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);

  useEffect(() => setEvents(initial), [initial]);

  useEffect(() => {
    let closed = false;
    let ping;
    const connect = () => {
      const ws = new WebSocket(wsUrl("/ws/events"));
      wsRef.current = ws;
      ws.onopen = () => {
        setConnected(true);
        ping = setInterval(() => { try { ws.send("ping"); } catch {} }, 30000);
      };
      ws.onclose = () => {
        setConnected(false);
        clearInterval(ping);
        if (!closed) setTimeout(connect, 4000);
      };
      ws.onerror = () => ws.close();
      ws.onmessage = (m) => {
        try {
          const data = JSON.parse(m.data);
          if (data.type === "event") {
            setEvents((prev) => [data.event, ...prev].slice(0, 30));
          }
        } catch {}
      };
    };
    connect();
    return () => { closed = true; clearInterval(ping); try { wsRef.current?.close(); } catch {} };
  }, []);

  return (
    <div className="card-surface flex flex-col" data-testid={testid}>
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
        <div className="flex items-center gap-2.5">
          <span className="relative flex h-2.5 w-2.5">
            <span className={`absolute inline-flex h-full w-full rounded-full ${connected ? "bg-emerald-400 ping-dot" : "bg-slate-500"}`} />
            <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${connected ? "bg-emerald-400" : "bg-slate-500"}`} />
          </span>
          <h3 className="font-display font-semibold text-sm tracking-wide text-white">LIVE CAMPAIGN ACTIVITY</h3>
        </div>
        {connected && (
          <span className="text-[10px] font-bold tracking-widest px-2 py-1 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/20" data-testid="live-badge">
            LIVE
          </span>
        )}
      </div>
      <div className="flex-1 overflow-y-auto max-h-[420px] divide-y divide-white/5">
        {events.length === 0 && (
          <div className="p-8 text-center text-sm text-muted-foreground">
            <Activity className="w-6 h-6 mx-auto mb-2 text-slate-600" />
            Waiting for live campaign events...
          </div>
        )}
        {events.map((e, i) => (
          <div key={e.id || i} className="px-5 py-3 flex items-start gap-3 hover:bg-white/[0.02] transition-colors" data-testid="live-feed-event">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-cyan-400 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm text-slate-200">
                <span className="text-slate-500 font-mono text-xs mr-2">{eventTime(e.timestamp)}</span>
                {e.label || e.event_type}
              </p>
              <p className="text-xs text-slate-500 mt-0.5">{e.location_name}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default LiveFeed;
